// src/components/AdminPortal/UserManagement/Roles.js
import { useState, useEffect } from "react";
import { FiPlus } from "react-icons/fi";
import { toast } from "react-toastify";
import Axios from "../../../utils/axios";
import "../../User.css";
import "../UserManagement.css";

const API_BASE = process.env.REACT_APP_AUTH_BASE_URL;

const Roles = ({ tenantId = 1 }) => {
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
    address: "",
    tenantId: tenantId,
    applicationCode: "WEB",
    fullName: "",
    phoneNumber: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // 🔹 Fetch all users of tenant
  const fetchRoles = async () => {
    try {
      const response = await Axios.get(`/api/user/getAllUsersOfTenant`);
      const list = response?.data?.data || response?.data || [];

      const mapped = list.map((user) => ({
        id: user.user_id || user.id || "",
        username: user.username || "",
        fullName: user.full_name || "",
        address: user.address || "",
        email: user.email || "",
        phoneNumber: user.phone_number || "",
      }));

      setRoles(mapped);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
      setRoles([]);
    }
  };

  // 🔹 Create new user
  const createRole = async (userData) => {
    try {
      const response = await Axios.post(
        `${API_BASE}/api/auth/register`,
        userData
      );
      return response?.data;
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
      return null;
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [tenantId]);

  // 🔹 Filtering
  const filteredRoles = roles.filter((role) => {
    const search = searchTerm.toLowerCase();
    return (
      role.username?.toLowerCase().includes(search) ||
      role.fullName?.toLowerCase().includes(search) ||
      role.address?.toLowerCase().includes(search) ||
      role.email?.toLowerCase().includes(search) ||
      role.phoneNumber?.toLowerCase().includes(search)
    );
  });

  // 🔹 Validation
  const validateForm = () => {
    let newErrors = {};
    if (!formData.username) newErrors.username = "Username is required";
    if (!formData.fullName) newErrors.fullName = "Full Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.password) newErrors.password = "Password is required";
    if (!formData.tenantId) newErrors.tenantId = "TenantId is required";
    if (!formData.applicationCode)
      newErrors.applicationCode = "Application Code is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔹 Handlers
  const handleNew = () => {
    setFormData({
      username: "",
      password: "",
      email: "",
      address: "",
      tenantId: tenantId,
      applicationCode: "WEB",
      fullName: "",
      phoneNumber: "",
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      await createRole(formData);
      await fetchRoles();
      toast.success("User created successfully!");
      setIsModalOpen(false);
    } catch (error) {
      toast.error(error?.message || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="table-container">
      <h2>Users</h2>
      <br />

      <div className="search-and-new">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <button onClick={() => setSearchTerm("")} className="clear-button">
          Clear
        </button>
        <button onClick={handleNew} className="new-button">
          <FiPlus />
        </button>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>Username</th>
            <th>Full Name</th>
            <th>Address</th>
            <th>Email</th>
            <th>Phone Number</th>
          </tr>
        </thead>
        <tbody>
          {filteredRoles.map((role) => (
            <tr key={role.id}>
              <td>{role.username}</td>
              <td>{role.fullName}</td>
              <td>{role.address}</td>
              <td>{role.email}</td>
              <td>{role.phoneNumber}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>New User</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="modal-close-button"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Username*</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                />
                {errors.username && (
                  <p className="text-danger">{errors.username}</p>
                )}
              </div>

              <div className="form-group">
                <label>Full Name*</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                />
                {errors.fullName && (
                  <p className="text-danger">{errors.fullName}</p>
                )}
              </div>

              <div className="form-group">
                <label>Email*</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
                {errors.email && <p className="text-danger">{errors.email}</p>}
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Password*</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                />
                {errors.password && (
                  <p className="text-danger">{errors.password}</p>
                )}
              </div>

              <div className="form-group">
                <label>Tenant ID*</label>
                <input
                  type="number"
                  name="tenantId"
                  value={formData.tenantId}
                  onChange={handleChange}
                />
                {errors.tenantId && (
                  <p className="text-danger">{errors.tenantId}</p>
                )}
              </div>

              <div className="form-group">
                <label>Application Code*</label>
                <input
                  type="text"
                  name="applicationCode"
                  value={formData.applicationCode}
                  onChange={handleChange}
                />
                {errors.applicationCode && (
                  <p className="text-danger">{errors.applicationCode}</p>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="save-button"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;
